<template>
  <div class="containers-page space-y-5">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Container Types</h1>
        <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
          {{ containers ? containers.length : 0 }} type{{ containers?.length !== 1 ? 's' : '' }} configured
        </p>
      </div>
      <button
        @click="openCreateModal"
        class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      >
        <span class="material-icons text-[18px]" aria-hidden="true">add</span>
        Add Type
      </button>
    </div>

    <!-- Table -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
            <tr>
              <th class="px-4 py-3 text-center text-sm font-semibold text-neutral-700 dark:text-neutral-300 w-16">#</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Container Type</th>
              <th class="px-4 py-3 text-center text-sm font-semibold text-neutral-700 dark:text-neutral-300">Max Volume</th>
              <th class="px-4 py-3 text-center text-sm font-semibold text-neutral-700 dark:text-neutral-300 w-24">Actions</th>
            </tr>
          </thead>
          <!-- Loading skeleton until the first Firestore snapshot lands -->
          <tbody v-if="!containers" class="divide-y divide-neutral-200 dark:divide-neutral-700">
            <tr v-for="n in 3" :key="n">
              <td v-for="col in 4" :key="col" class="px-4 py-3">
                <div class="h-5 rounded animate-pulse bg-neutral-100 dark:bg-neutral-700"></div>
              </td>
            </tr>
          </tbody>
          <tbody v-else class="divide-y divide-neutral-200 dark:divide-neutral-700">
            <tr
              v-for="(container, idx) in containers"
              :key="container.id"
              class="hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
            >
              <td class="px-4 py-3 text-center text-sm text-neutral-400 dark:text-neutral-500">{{ idx + 1 }}</td>
              <td class="px-4 py-3">
                <div class="flex items-center gap-2">
                  <div class="w-7 h-7 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center flex-shrink-0">
                    <span class="material-icons text-sm text-primary-600 dark:text-primary-400" aria-hidden="true">inventory_2</span>
                  </div>
                  <span class="text-sm font-medium text-neutral-900 dark:text-neutral-100">{{ container.containerType }}</span>
                </div>
              </td>
              <td class="px-4 py-3 text-center">
                <span class="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{{ container.maxVolume }}</span>
                <span class="text-xs text-neutral-400 ml-0.5">L</span>
              </td>
              <td class="px-4 py-3 text-center">
                <div class="flex items-center gap-1 justify-center">
                  <button
                    @click="openEditModal(container)"
                    class="p-2 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/30 transition-all duration-150"
                    title="Edit container type"
                    aria-label="Edit container type"
                  >
                    <span class="material-icons text-[18px]" aria-hidden="true">edit</span>
                  </button>
                  <button
                    @click="confirmDelete(container)"
                    class="p-2 rounded-lg text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/30 transition-all duration-150"
                    title="Delete container type"
                    aria-label="Delete container type"
                  >
                    <span class="material-icons text-[18px]" aria-hidden="true">delete_outline</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="containers && !containers.length" class="py-12 text-center">
          <span class="material-icons text-4xl text-neutral-300 dark:text-neutral-600" aria-hidden="true">inventory_2</span>
          <p class="text-neutral-500 dark:text-neutral-400 mt-2">No container types configured</p>
        </div>
      </div>
    </div>

    <!-- Add / Edit Container Modal -->
    <Modal :show="showContainerModal" :title="editingContainer ? 'Edit Container Type' : 'New Container Type'" size="sm" @close="closeModal">
      <form id="container-form" @submit.prevent="handleSubmit" class="space-y-4">
        <div>
          <label for="container-type" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Container Type</label>
          <input
            id="container-type"
            v-model="containerType"
            type="text"
            required
            placeholder="e.g. 240L Wheelie Bin"
            class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div>
          <label for="container-max-volume" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Max Volume (L)</label>
          <input
            id="container-max-volume"
            v-model="maxVolume"
            type="number"
            required
            min="1"
            placeholder="e.g. 240"
            class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent tabular-nums"
          />
        </div>

        <div v-if="formError" role="alert" class="flex items-center gap-2 px-3 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg">
          <span class="material-icons text-red-500 text-sm" aria-hidden="true">error</span>
          <p class="text-xs text-red-700 dark:text-red-400">{{ formError }}</p>
        </div>
      </form>

      <template #footer>
        <div class="flex items-center justify-end gap-2">
          <button
            @click="closeModal"
            class="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-600 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="container-form"
            :disabled="isSaving || !containerType || !maxVolume"
            class="px-4 py-2 text-sm font-semibold text-white bg-primary-700 rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isSaving ? 'Saving...' : (editingContainer ? 'Save Changes' : 'Add Type') }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Delete Confirmation -->
    <ConfirmDialog
      :show="showDeleteConfirm"
      title="Delete container type"
      :message="`Delete '${containerToDelete?.containerType}'? This cannot be undone.`"
      confirm-label="Delete"
      :loading="isDeleting"
      danger
      @confirm="handleDelete"
      @cancel="showDeleteConfirm = false"
    />
  </div>
</template>

<script>
import getCollection from '@/composables/getCollection';
import useCollection from '@/composables/useCollection';
import useToast from '@/composables/useToast';
import { ref } from 'vue';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';
import Modal from '@/components/ui/Modal.vue';
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue';
import { useStore } from 'vuex';

export default {
  components: { Modal, ConfirmDialog },
  setup() {
    const store = useStore();
    store.commit("setCurrentRoute", "Containers");
    const { showToast } = useToast();

    const { documents: containers } = getCollection('containers');
    const { addDoucment, error } = useCollection('containers');

    const showContainerModal = ref(false);
    const containerType = ref('');
    const maxVolume = ref('');
    const formError = ref(null);
    const editingContainer = ref(null);
    const isSaving = ref(false);

    const showDeleteConfirm = ref(false);
    const containerToDelete = ref(null);
    const isDeleting = ref(false);

    const openCreateModal = () => {
      editingContainer.value = null;
      containerType.value = '';
      maxVolume.value = '';
      formError.value = null;
      showContainerModal.value = true;
    };

    const openEditModal = (container) => {
      editingContainer.value = container;
      containerType.value = container.containerType;
      maxVolume.value = container.maxVolume;
      formError.value = null;
      showContainerModal.value = true;
    };

    const closeModal = () => {
      showContainerModal.value = false;
      formError.value = null;
      editingContainer.value = null;
    };

    const handleSubmit = async () => {
      if (isSaving.value) return;
      isSaving.value = true;
      formError.value = null;
      try {
        const payload = {
          containerType: containerType.value,
          maxVolume: Number(maxVolume.value),
        };
        if (editingContainer.value) {
          await updateDoc(doc(db, 'containers', editingContainer.value.id), payload);
          closeModal();
          showToast('success', 'Container type updated');
        } else {
          await addDoucment(payload);
          if (error.value) {
            throw new Error(error.value);
          }
          closeModal();
          showToast('success', 'Container type added');
        }
      } catch (err) {
        console.error('Failed to save container type:', err);
        formError.value = 'Could not save this container type. Please try again.';
        showToast('error', `Failed to ${editingContainer.value ? 'update' : 'add'} container type. Please try again.`);
      } finally {
        isSaving.value = false;
      }
    };

    const confirmDelete = (container) => {
      containerToDelete.value = container;
      showDeleteConfirm.value = true;
    };

    const handleDelete = async () => {
      if (!containerToDelete.value || isDeleting.value) return;
      isDeleting.value = true;
      try {
        await deleteDoc(doc(db, 'containers', containerToDelete.value.id));
        showDeleteConfirm.value = false;
        containerToDelete.value = null;
        showToast('success', 'Container type deleted');
      } catch (err) {
        console.error('Failed to delete container type:', err);
        showToast('error', 'Failed to delete container type. Please try again.');
      } finally {
        isDeleting.value = false;
      }
    };

    return {
      containers, showContainerModal, containerType, maxVolume, formError,
      editingContainer, isSaving,
      showDeleteConfirm, containerToDelete, isDeleting,
      openCreateModal, openEditModal, closeModal, handleSubmit,
      confirmDelete, handleDelete,
    };
  },
};
</script>

<style scoped>
.containers-page > * {
  animation: fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both;
}
.containers-page > *:nth-child(1) { animation-delay: 0s; }
.containers-page > *:nth-child(2) { animation-delay: 0.06s; }

@media (prefers-reduced-motion: reduce) {
  .containers-page > * {
    animation: none;
  }
}

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
